import React, { useState, useEffect, useCallback } from 'react'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { useLazyQuery, useMutation } from '@apollo/client'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { t } from 'i18next'
import { useNavigate } from 'react-router-dom'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { EllipsisVerticalIcon } from '@heroicons/react/16/solid'
import { EyeIcon, ArrowLeftIcon, PencilIcon, PlusIcon } from '@heroicons/react/20/solid'
import { Input, InputGroup } from 'components/input'
import { Select } from 'components/select'
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { GET_EXPLORE_ITEMS } from 'graphql/exploreItems/queries'
import { UPDATE_ACTIVE_EXPLORE_ITEM } from 'graphql/exploreItems/mutations'
import loadingImage from '../../assets/images/boat-loading.gif'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import { formatDateOnly } from 'utils/common'
import { getPageRange } from 'utils/paginationHelper'

// Category options - will be populated from API
const CATEGORY_OPTIONS = [
    { value: '', label: 'All Categories' },
    { value: 'ATR', label: 'Attraction' },
    { value: 'HOT', label: 'Hotel' },
    { value: 'CAR', label: 'Car Rental' },
]

// Country options - will be populated from API
const COUNTRY_OPTIONS = [
    { value: '', label: 'All Countries' },
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'BH', label: 'Bahrain' },
    { value: 'KW', label: 'Kuwait' },
    { value: 'OM', label: 'Oman' },
    { value: 'QA', label: 'Qatar' },
    { value: 'SA', label: 'Saudi Arabia' },
]

export default function ExploreItems() {
    const navigate = useNavigate()
    
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [confirmMessage, setConfirmMessage] = useState('')
    const [itemToToggle, setItemToToggle] = useState(null)

    const [exploreItemsList, setExploreItemsList] = useState(null)
    const [exploreItemsCount, setExploreItemsCount] = useState(0)
    
    // Dynamic options from API data
    const [availableCategories, setAvailableCategories] = useState(CATEGORY_OPTIONS)
    const [availableCountries, setAvailableCountries] = useState(COUNTRY_OPTIONS)
    const [availableCurrencies, setAvailableCurrencies] = useState([])

    // Filters
    const [filters, setFilters] = useState({
        countryCode: '',
        categoryCode: '',
        searchString: '',
    })

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(exploreItemsCount / rowsPerPage)

    const [getExploreItemsList, { loading: exploreItemsListLoading, error: exploreItemsListError, refetch }] = useLazyQuery(GET_EXPLORE_ITEMS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getExploreItems && data.getExploreItems.success) {
                const items = data.getExploreItems.explore_item || []
                setExploreItemsList(items)
                
                // Use the total_count from the API response
                setExploreItemsCount(data.getExploreItems.total_count || 0)
                
                // If no items on current page and we're not on page 0, go to previous page
                if (items.length === 0 && page > 0) {
                    setPage(page - 1)
                    return
                }
                
                // Extract unique categories, countries, and currencies from the data
                const uniqueCategories = new Set()
                const uniqueCountries = new Set()
                const uniqueCurrencies = new Set()
                
                items.forEach(item => {
                    if (item.category_code) {
                        uniqueCategories.add(item.category_code)
                    }
                    if (item.country_code) {
                        uniqueCountries.add(item.country_code)
                    }
                    if (item.content?.pricing?.currency) {
                        uniqueCurrencies.add(item.content.pricing.currency)
                    }
                })
                
                // Keep all static options available for filtering, don't limit to only data that exists
                // Users should be able to filter by any category/country even if no data exists for it
                setAvailableCategories(CATEGORY_OPTIONS)
                setAvailableCountries(COUNTRY_OPTIONS)
                
                const currencyOptions = Array.from(uniqueCurrencies).map(code => ({
                    value: code,
                    label: code
                }))
                setAvailableCurrencies(currencyOptions)
            } else if (data.getExploreItems && !data.getExploreItems.success) {
                setErrorMessage(data.getExploreItems.message || 'Failed to fetch explore items')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message)
            setShowErrDialog(true)
            // Ensure static options are available even if API fails
            setAvailableCategories(CATEGORY_OPTIONS)
            setAvailableCountries(COUNTRY_OPTIONS)
        },
    })

    const [updateActiveStatus, { loading: updateLoading }] = useMutation(UPDATE_ACTIVE_EXPLORE_ITEM, {
        onCompleted: data => {
            if (data.updateActiveExploreItem.success) {
                setSuccessMessage('Status updated successfully')
                setShowSucDialog(true)
                searchExploreItems() // Refresh the list
            } else {
                setErrorMessage(data.updateActiveExploreItem.message || 'Failed to update status')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    // Search explore items
    const searchExploreItems = useCallback(() => {
        getExploreItemsList({
            variables: {
                countryCode: filters.countryCode || null,
                categoryCode: filters.categoryCode || null,
                searchString: filters.searchString || null,
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
            },
        })
    }, [getExploreItemsList, filters.countryCode, filters.categoryCode, filters.searchString, page, rowsPerPage])

    useEffect(() => {
        searchExploreItems()
    }, [searchExploreItems])

    // Refetch data when component mounts (e.g., returning from edit form)
    useEffect(() => {
        if (refetch) {
            refetch()
        }
    }, [refetch])

    const handlePageChange = newPage => {
        // Prevent going beyond valid page range
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage)
        }
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
        setShowErrDialog(false)
        setErrorMessage('')
        setShowConfirmDialog(false)
        setConfirmMessage('')
        setItemToToggle(null)
    }

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }))
        setPage(0) // Reset to first page when filters change
    }

    const handleStatusToggle = (itemId, currentStatus) => {
        // Show confirmation dialog for both activation and deactivation
        setItemToToggle({ itemId, currentStatus })
        if (currentStatus) {
            setConfirmMessage('Are you sure you want to deactivate this explore item? This will make it unavailable to users.')
        } else {
            setConfirmMessage('Are you sure you want to activate this explore item? This will make it available to users.')
        }
        setShowConfirmDialog(true)
    }

    const handleConfirmAction = () => {
        if (itemToToggle) {
            updateActiveStatus({
                variables: {
                    itemId: itemToToggle.itemId,
                    isActive: !itemToToggle.currentStatus,
                },
            })
        }
        setShowConfirmDialog(false)
        setItemToToggle(null)
        setConfirmMessage('')
    }

    const handleCancelAction = () => {
        setShowConfirmDialog(false)
        setItemToToggle(null)
        setConfirmMessage('')
    }

    const getCategoryLabel = (categoryCode) => {
        const category = CATEGORY_OPTIONS.find(opt => opt.value === categoryCode)
        return category ? category.label : categoryCode
    }

    const getCountryLabel = (countryCode) => {
        const country = COUNTRY_OPTIONS.find(opt => opt.value === countryCode)
        return country ? country.label : countryCode
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                                        <PlusIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Explore Items')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Manage explore items for attractions, hotels, and car rentals')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <button
                                    onClick={() => navigate('/explore-items/add')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Item')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('Search')}
                    </label>
                    <InputGroup>
                        <Input
                            type="text"
                            placeholder={t('Search explore items...')}
                            value={filters.searchString}
                            onChange={e => handleFilterChange('searchString', e.target.value)}
                        />
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </InputGroup>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('Country')}
                    </label>
                    <Select
                        value={filters.countryCode}
                        onChange={e => handleFilterChange('countryCode', e.target.value)}
                    >
                        {availableCountries.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('Category')}
                    </label>
                    <Select
                        value={filters.categoryCode}
                        onChange={e => handleFilterChange('categoryCode', e.target.value)}
                    >
                        {availableCategories.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="flex items-end">
                    <button
                        onClick={searchExploreItems}
                        type="button"
                        className="inline-flex items-center gap-x-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[.9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                        <ArrowPathIcon className="-ml-0.5 h-4 w-4" />
                        {t('Refresh')}
                    </button>
                </div>
            </div>

            {exploreItemsListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {exploreItemsListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch explore items!')}</div>
                </div>
            )}
            {!exploreItemsListLoading && !exploreItemsListError && exploreItemsList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!exploreItemsListLoading && !exploreItemsListError && exploreItemsList?.length > 0 && (
                <>
                    <Divider className="mt-5" />
                    <div className="table-custom flex justify-center items-center bg-white p-0">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Sno')}</TableHeader>
                                    <TableHeader>{t('Image')}</TableHeader>
                                    <TableHeader>{t('Title')}</TableHeader>
                                    <TableHeader>{t('Category')}</TableHeader>
                                    <TableHeader>{t('Country')}</TableHeader>
                                    <TableHeader>{t('Price')}</TableHeader>
                                    <TableHeader>{t('Validity')}</TableHeader>
                                    <TableHeader>{t('Active')}</TableHeader>
                                    <TableHeader className="flex justify-end">{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exploreItemsList.map((item, index) => (
                                    <TableRow
                                        key={item.item_id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/explore-items/details/${item.item_id}`)}
                                        title={`Explore Item #${item.item_id}`}
                                    >
                                        <TableCell>
                                            <span className="text-sm font-semibold text-gray-900">{index + 1}</span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2 items-center">
                                                <ImageDisplay
                                                    s3Key={item.content?.cover_image?.mobile?.s3_key}
                                                    file=""
                                                    classStyle="w-[80px] h-[50px] object-cover rounded-md border-[1px] border-primary/[.3]"
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {item.content?.title?.en || 'N/A'}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {item.content?.title?.ar || ''}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge color="blue">
                                                {getCategoryLabel(item.category_code)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-sm text-gray-900">
                                                {getCountryLabel(item.country_code)}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {item.content?.pricing?.price_from 
                                                        ? `${item.content.pricing.price_from} ${item.content.pricing.currency || ''}`
                                                        : 'N/A'
                                                    }
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {formatDateOnly(item.valid_from)} -{' '}
                                                    {formatDateOnly(item.valid_to)}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={item.is_active ? 'emerald' : 'rose'}>
                                                {item.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    className="cursor-pointer"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/explore-items/details/${item.item_id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/explore-items/edit/${item.item_id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            handleStatusToggle(item.item_id, item.is_active)
                                                        }}
                                                        disabled={updateLoading}
                                                    >
                                                        <DropdownLabel>
                                                            {item.is_active ? t('Deactivate') : t('Activate')}
                                                        </DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page >= totalPages - 1 || totalPages === 0}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleDialogClose} />
            )}
            {showConfirmDialog && (
                <ConfirmDialog 
                    status={true} 
                    title={itemToToggle?.currentStatus ? t('Confirm Deactivation') : t('Confirm Activation')} 
                    message={confirmMessage}
                    onPositive={handleConfirmAction}
                    onNegative={handleCancelAction}
                    positiveActionText={itemToToggle?.currentStatus ? t('Deactivate') : t('Activate')}
                    negativeActionText={t('Cancel')}
                />
            )}
        </>
    )
}
